import { ArrowLeft, ArrowRight, Button, Delete, Heading, Input } from '@core/ui';
import { t } from 'i18next';
import { Duration } from 'luxon';
import { useEffect, useState } from 'react';
import { ChapterTimelineEvent, Timeline } from '../../../parser';
import { TimestampInput } from '../../molecules';

export const ChapterEditorOrganism = ({ timeline, currentTime, onSeek }: { timeline: Timeline; currentTime: Duration; onSeek: (seek: Duration) => void }) => {
    const [previousChapter, setPreviousChapter] = useState<ChapterTimelineEvent>();
    const [nextChapter, setNextChapter] = useState<ChapterTimelineEvent>();
    const [currentChapter, setCurrentChapter] = useState<ChapterTimelineEvent>();
    const [currentChapterIndex, setCurrentChapterIndex] = useState<number>(-1);
    const [currentTitle, setCurrentTitle] = useState<string>('');

    useEffect(() => {
        if (!timeline?.chapters) {
            setPreviousChapter(undefined);
            setCurrentChapter(undefined);
            setNextChapter(undefined);
            return;
        }

        let prevChapter: ChapterTimelineEvent | undefined = undefined;
        let currChapter: ChapterTimelineEvent | undefined = undefined;
        let currIndex: number = -1;
        let nextChapter: ChapterTimelineEvent | undefined = undefined;
        for (let i = 0; i < timeline.chapters.length; i++) {
            if (timeline.chapters[i].timestamp <= currentTime) {
                if (i > 0) {
                    prevChapter = timeline.chapters[i - 1];
                }

                currChapter = timeline.chapters[i];
                currIndex = i;

                if (i < timeline.chapters.length - 1) {
                    nextChapter = timeline.chapters[i + 1];
                }
            }
        }

        if (!currChapter && timeline.chapters.length > 0) {
            nextChapter = timeline.chapters[0];
        }

        setPreviousChapter(prevChapter);
        setCurrentChapter(currChapter);
        setCurrentChapterIndex(currIndex);
        setCurrentTitle(currChapter?.title || '');
        setNextChapter(nextChapter);
    }, [timeline?.chapters, currentTime]);

    useEffect(() => {
        if (!currentChapter) {
            return;
        }

        const timeout = setTimeout(() => {
            currentChapter.changed = currentChapter.changed || currentChapter.title !== currentTitle;
            currentChapter.title = currentTitle;

            timeline.changed = timeline.changed || currentChapter.changed;
        }, 250);
        return () => clearTimeout(timeout);
    }, [currentTitle, currentChapter]);

    const onDelete = () => {
        if (currentChapterIndex === -1) {
            return;
        }

        timeline.chapters.splice(currentChapterIndex, 1);
        timeline.chapters = [...timeline.chapters];
    };

    return (
        <div className="bg-gray-100 border border-gray-500 flex flex-col gap-4 pb-2">
            {currentChapter && currentChapterIndex !== -1 && (
                <>
                    <div className="flex-1 flex flex-col bg-white rounded-b-webinarsub p-4">
                        <div className="flex items-center justify-between gap-2 mb-4">
                            <Heading variant="h2">{t('Editor.Chapter.Heading')}</Heading>
                            <Button variant="warning-outline" onClick={onDelete} iconbtn classNames="ms-auto">
                                <Delete classNames="size-6 flex-none stroke-current" />
                                <span className="sr-only">{t('Editor.Chapter.Button.Delete')}</span>
                            </Button>
                        </div>
                        <div className="flex-1 flex items-center justify-center gap-x-2">
                            <Button
                                variant="primary"
                                iconbtn
                                onClick={() => onSeek(previousChapter?.timestamp || Duration.fromMillis(0))}
                                disabled={!previousChapter}
                            >
                                <span className="sr-only">{t('Chapter.Button.Previous')}</span>
                                <ArrowLeft classNames="size-6 fill-current" />
                            </Button>
                            <Input
                                type="text"
                                label={t('Editor.Chapter.Title.Label')}
                                classNames="flex-1 min-w-40"
                                value={currentTitle}
                                onChange={(e) => setCurrentTitle(e.target.value)}
                            ></Input>
                            <Button variant="primary" iconbtn onClick={() => onSeek(nextChapter?.timestamp || Duration.fromMillis(0))} disabled={!nextChapter}>
                                <span className="sr-only">{t('Chapter.Button.Next')}</span>
                                <ArrowRight classNames="size-6 fill-current" />
                            </Button>
                        </div>
                    </div>
                    <TimestampInput timestamp={currentChapter.timestamp} onChange={(timestamp) => (currentChapter.timestamp = timestamp)} />
                </>
            )}
        </div>
    );
};
